import { useCallback, useContext, useEffect, useState } from "react";

import { useTranslation } from "react-i18next";

import { css } from "@emotion/react";

import { BannerType, BannerView } from "@dotnet/telematics-design-system";

import Autocomplete from "components/DesignComponents/Autocomplete";
import Dialog from "components/DesignComponents/Dialog";
import InputField, {
    INPUT_FIELD_SIZES
} from "components/DesignComponents/InputField";
import Dropzone from "components/Dropzone";
import CompanyDropdown from "components/QueryDropdown/CompanyDropdown";
import DropzoneContext from "context/dropzone/dropzoneContext";
import ParamsContext from "context/params/paramsContext";
import {
    Company,
    DROPDOWN_SIZES,
    ENDPOINTS,
    EventData,
    FILE_NAME_MAX_LENGTH,
    FILE_TYPES,
    FILE_UPLOAD_STATUS,
    Filter,
    globalCompany,
    MAX_FILES_COUNT,
    maxDialogHeight,
    maxDialogWidth,
    SORT_RESOURCES,
    TABLE_NAMES,
    useApi,
    useMediaQueries
} from "shared";

import { DialogProps, FILE_ACTIONS } from "../../../types";
import { useFileTypes } from "../hooks";
import { FileType } from "../types";
import FileUploadActions from "./FileUploadContent/FileUploadActions/FileUploadActions";
import FileUploadResult from "./FileUploadContent/FileUploadResult/FileUploadResult";

const UploadFile = ({
    isOpen,
    close,
    selectedType,
    customCompanies,
    onEnter,
    onExited,
    successCallback
}: DialogProps) => {
    const { rootCompany, setReloadItems } = useContext(ParamsContext);

    const { multipleFiles, setStatus, removeAllFiles, setMessage } =
        useContext(DropzoneContext);

    const { t } = useTranslation();
    const { handleResponse, postData } = useApi();
    const { visibleTypes } = useFileTypes();
    const { toMd, fromMd } = useMediaQueries();

    const [counter, setCounter] = useState(0);

    const [companies, setCompanies] = useState(
        customCompanies || [rootCompany as Filter]
    );

    const [description, setDescription] = useState("");

    const [fileType, setFileType] = useState<FileType | null>(
        selectedType || null
    );

    const [isUploaded, setIsUploaded] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    const isFmObdFileTypeSelected = fileType?.value === FILE_TYPES.FmObdChipFw;

    useEffect(() => {
        if (customCompanies) return;

        if (isFmObdFileTypeSelected) {
            // If FM OBD Chip firmware file type is selected, set the Teltonika Files company
            setCompanies([globalCompany]);
        } else {
            // In other cases, set root company
            setCompanies([rootCompany as Filter]);
        }
    }, [customCompanies, isFmObdFileTypeSelected, rootCompany]);

    const isReady =
        companies?.length > 0 && fileType && multipleFiles.length > 0;

    const getFormData = useCallback(() => {
        const payload: any[] = [];

        if (isReady) {
            multipleFiles.forEach(fileObj => {
                const { file, id } = fileObj;

                const formData = new FormData();

                const companyIds = companies.map(company => company?.id);

                companyIds.forEach((singleID, index) => {
                    formData.append(
                        `company_ids[${index}]`,
                        JSON.stringify(singleID)
                    );
                });

                formData.append("type", fileType.value);
                formData.append("file", file);

                description.trim().length > 0 &&
                    formData.append("description", description);

                payload.push({ formData, id });
            });

            return payload;
        }
    }, [companies, description, fileType, isReady, multipleFiles]);

    const handleCloseDialog = () => close(FILE_ACTIONS.UploadFiles);

    const handleChangeFileType = (type: FileType | null) => {
        setFileType(type);
        removeAllFiles();
    };

    const handleCompanyChange = (companyValue: Company[]) =>
        setCompanies(companyValue);

    const handleDescriptionChange = (e: { target: EventData }) => {
        const { value } = e.target;
        setDescription(value);
    };

    const handleSubmit = async () => {
        if (isReady) {
            try {
                setIsLoading(true);

                let successCount = 0;

                const payloadData = {
                    resource: ENDPOINTS.Files,
                    data: getFormData()
                };

                if (payloadData.data) {
                    for (const singlePayloadData of payloadData.data) {
                        const { formData, id } = singlePayloadData;

                        try {
                            await postData(ENDPOINTS.Files, formData);
                            setStatus(FILE_UPLOAD_STATUS.Success, id);
                            successCount++;
                        } catch (error) {
                            setStatus(FILE_UPLOAD_STATUS.Failed, id, error);
                        }
                    }

                    setCounter(successCount);
                }

                if (successCount > 0) {
                    setReloadItems(TABLE_NAMES.Files);
                    successCallback?.();
                }
            } catch (error) {
                handleResponse(error);
            }

            setIsUploaded(true);
            setMessage([], true);
        }

        setIsLoading(false);
    };

    const resetDialogWindow = (fullReset: boolean) => {
        setIsUploaded(false);
        setCounter(0);
        removeAllFiles();

        if (fullReset) {
            setCompanies(customCompanies || [rootCompany as Filter]);
            setFileType(selectedType || null);
            setDescription("");
        }
    };

    return (
        <Dialog
            data-testid="upload-file-dialog"
            title={t("Dialog##upload file")}
            description={t("Dialog##upload file description", {
                count: MAX_FILES_COUNT
            })}
            isTitleSeparator
            isActionsSeparator
            open={isOpen}
            close={handleCloseDialog}
            submit={handleSubmit}
            actions={
                <FileUploadActions
                    isUploaded={isUploaded}
                    isLoading={isLoading}
                    isReady={isReady}
                    close={handleCloseDialog}
                    resetDialog={() => resetDialogWindow(false)}
                />
            }
            slotProps={{
                transition: {
                    onEnter: () => onEnter?.(),
                    onExited: () => {
                        resetDialogWindow(true);
                        onExited?.();
                    }
                }
            }}
            css={css(fromMd && [maxDialogWidth, maxDialogHeight])}
            extendToMaxWidth={toMd}
        >
            {isUploaded ? (
                <FileUploadResult
                    fileNumber={multipleFiles.length}
                    successCounter={counter}
                    fileType={fileType}
                    companies={companies}
                    description={description}
                />
            ) : (
                <div
                    css={css({
                        "> div:first-of-type": {
                            marginTop: "8px"
                        },
                        "> div": {
                            marginBottom: "16px"
                        }
                    })}
                >
                    {fileType?.hasInfo && (
                        <BannerView
                            bannerType={BannerType.Info}
                            title={t(
                                `Dialog##file upload##banner text##${fileType.value}`
                            )}
                        />
                    )}

                    <Autocomplete
                        labelLeft={t("Table##file type")}
                        textFieldParams={{ placeholder: t("Dialog##select") }}
                        data-testid="file-type-dropdown"
                        fullWidth
                        isRequired
                        readOnly={isLoading}
                        value={fileType}
                        size={DROPDOWN_SIZES.Medium}
                        options={visibleTypes}
                        getOptionLabel={option => t(`General##${option.value}`)}
                        isOptionEqualToValue={(option, value) =>
                            option.value === value.value
                        }
                        onChange={(_, val) => handleChangeFileType(val)}
                    />

                    <CompanyDropdown
                        multiple
                        data-testid="company-file-upload-dropdown"
                        value={companies}
                        isRequired
                        change={(_, val) => handleCompanyChange(val)}
                        isRootRequired
                        textFieldParams={{
                            placeholder:
                                companies.length > 0 ? "" : t("Dialog##select")
                        }}
                        readOnly={isFmObdFileTypeSelected || isLoading}
                    />

                    <InputField
                        data-testid="description-input-field"
                        size={INPUT_FIELD_SIZES.Medium}
                        fullWidth
                        readOnly={isLoading}
                        onChange={handleDescriptionChange}
                        value={description}
                        name={SORT_RESOURCES.Description}
                        labelLeft={`${t("Table##description")} (${t(
                            "Dialog##optional"
                        )})`}
                        placeholder={`${t("General##enter here")}`}
                    />
                </div>
            )}

            <Dropzone
                data-testid="file-upload-dropzone"
                supportedFormats={fileType?.extensions || []}
                countCsv
                isDisabled={!fileType}
                isMultiple
                maxFileNameLength={FILE_NAME_MAX_LENGTH}
                isLoading={isLoading}
                isUploaded={isUploaded}
                maxSizeBytes={50000000} // 50MB
            />
        </Dialog>
    );
};

export default UploadFile;
export * from "../hooks";
